'use client';

// React & Core Hook Imports
import React, { useEffect, useState, useRef } from 'react';

// Third-Party Library Imports
import classNames from 'classnames';
import parse from 'html-react-parser';

// Style Imports
import styles from './toast.module.scss';

interface ToastProps {
  id: string;
  type: 'error' | 'success' | 'notice';
  label?: boolean;
  message: string | React.ReactNode; // Accepts both strings and JSX
  autoFadeOut?: boolean;
  fadeOutTime?: number;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  label = false,
  message,
  autoFadeOut = false,
  fadeOutTime = 3000,
  dismissible = false,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (autoFadeOut) {
      timeout = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          if (onDismiss) {
            onDismiss();
          }
        }, 500); // Extra time for fade-out animation
      }, fadeOutTime);
    }
    return () => clearTimeout(timeout);
  }, [autoFadeOut, fadeOutTime, onDismiss]);

  useEffect(() => {
    if (isVisible && dismissible && dismissButtonRef.current) {
      dismissButtonRef.current.focus();
    }
  }, [isVisible, dismissible]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onDismiss) {
        onDismiss();
      }
    }, 500); // Extra time for fade-out animation
  };

  const getLabelText = () => {
    switch (type.toLowerCase()) {
      case 'error':
        return <strong className={styles.label}>Error: </strong>;
      default:
        return null;
    }
  };

  return (
    <div
      className={classNames(styles.toast, {
        [styles.fadeOut]: !isVisible,
        [styles.dismissible]: dismissible,
        [styles.alertError]: type === 'error',
        [styles.alertSuccess]: type === 'success',
      })}
      id={id}
      {...(type === 'error'
        ? { role: 'alert' }
        : { 'aria-live': 'polite' })}
    >
      <span className={styles.message}>
        {label && getLabelText()}
        {typeof message === 'string' ? parse(message) : message} {/* Handles both string and JSX */}
      </span>
      {dismissible && (
        <button
          ref={dismissButtonRef}
          className={styles.dismissButton}
          type='button'
          onClick={handleDismiss}
          aria-label='Dismiss'
        >
          &times;
        </button>
      )}
    </div>
  );
};

export default Toast;
